import { Injectable } from '@angular/core';
import { AlertController, AlertOptions } from '@ionic/angular';
import { Observable, from, of } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { LocalStorageService } from './local-storage.service';
import { Browser } from '@capacitor/browser';

interface Announcement {
  id: string;
  title: string;
  message: string;
  startDate: string;
  endDate: string;
  informationLink?: string;
}

const ANNOUNCEMENTS: Announcement[] = [
  {
    id: 'new-partnership-update',
    title: 'Announcing our new partnership with Bennett’s Petroleum and Bonney Energy',
    message:
      'RACT members can now enjoy a discount of 6 cents off per litre at participating sites operated by Bennett’s Petroleum and Bonney Energy. To find out more and see a list of participating sites select "More Information" below.',
    startDate: '2024-04-01T00:00:00.000Z',
    endDate: '2025-06-06T23:59:59.999Z',
    informationLink: 'https://www.ract.com.au/membership/related-articles/fuel-discount-partnership',
  },
  {
    id: 'new-api-update',
    title: 'RACT members save with Bennett’s Petroleum & Bonney Energy*',
    message: '',
    startDate: '2025-11-19T00:00:00.000Z',
    endDate: '2026-04-01T23:59:59.999Z',
    informationLink: 'https://www.ract.com.au/membership/member-rewards/cars-and-driving/fuel-discount',
  },
];

const STORAGE_KEY = 'dismissed-announcements';

@Injectable({
  providedIn: 'root',
})
export class MessagingService {
  constructor(private alertController: AlertController, private localStorageService: LocalStorageService) {}

  /**
   * Shows an alert with the specified configuration
   */
  showAlert(header: string, message: string, link?: string): Observable<void> {
    return from(this.showAlertAsync(header, message, link));
  }

  /**
   * Shows an alert with the specified configuration (async version)
   */
  private async showAlertAsync(header: string, message: string, link?: string): Promise<void> {
    const buttons: AlertOptions['buttons'] = ['OK'];

    if (link) {
      buttons.unshift({
        text: 'More Information',
        handler: async () => {
          await Browser.open({ url: link });
        },
      });
    }

    const alert = await this.alertController.create({
      header,
      message,
      buttons,
    });

    await alert.present();
    await alert.onDidDismiss();
  }

  /**
   * Checks if an announcement should be shown based on its date range and dismissal status
   */
  private shouldShowAnnouncement(announcement: Announcement): Observable<boolean> {
    // Check date range
    const now = new Date();
    const startDate = new Date(announcement.startDate);
    const endDate = new Date(announcement.endDate);
    const isWithinDateRange = now >= startDate && now <= endDate;

    if (!isWithinDateRange) {
      return of(false);
    }

    // Check if announcement has been dismissed
    return this.localStorageService.get(STORAGE_KEY).pipe(
      map((data) => {
        const dismissedAnnouncements: string[] = data || [];
        return !dismissedAnnouncements.includes(announcement.id);
      })
    );
  }

  /**
   * Marks an announcement as dismissed
   */
  private markAsDismissed(announcementId: string): Observable<void> {
    return this.localStorageService.get(STORAGE_KEY).pipe(
      switchMap((data) => {
        const dismissedAnnouncements: string[] = data?.value || [];
        if (!dismissedAnnouncements.includes(announcementId)) {
          dismissedAnnouncements.push(announcementId);
          return this.localStorageService.set(STORAGE_KEY, dismissedAnnouncements).pipe(map(() => void 0));
        }
        return of(void 0);
      })
    );
  }

  /**
   * Shows a RACT announcement
   */
  private showAnnouncement(announcement: Announcement): Observable<void> {
    return this.showAlert(announcement.title, announcement.message, announcement.informationLink).pipe(
      switchMap(() => this.markAsDismissed(announcement.id))
    );
  }

  /**
   * Checks and shows all RACT announcements the user has not seen yet
   *
   * The function loops over the announcements to make sure only one shows up at a time. It will show an announcement and moves to the next one once the current one is dismissed
   */
  checkRactAnnouncements(index: number = 0): Observable<void> {
    if (index >= ANNOUNCEMENTS.length) {
      return of(void 0);
    }

    const announcement = ANNOUNCEMENTS[index];

    return this.shouldShowAnnouncement(announcement).pipe(
      switchMap((shouldShow) => {
        if (shouldShow) {
          return this.showAnnouncement(announcement).pipe(switchMap(() => this.checkRactAnnouncements(index + 1)));
        }

        // Show next announcement if the current one has been dismissed
        return this.checkRactAnnouncements(index + 1);
      })
    );
  }
}
